import { useEffect, useRef, useState } from "react";
import { SortableContext, verticalListSortingStrategy, arrayMove } from "@dnd-kit/sortable";
import { restrictToParentElement, restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { DndContext, closestCenter, useSensor, useSensors, DragEndEvent, MouseSensor, TouchSensor } from "@dnd-kit/core";

import { IOrdenacaoCategoriaItem, IOrdenacaoFonteItem, IUpdateFonteFavorita, IUpdateOrdenacaoFontes } from "../../interfaces";

import { EmitirErro } from "../../../../../../../../services/EmitirErro";
import FavoritasFonteItem from "../FavoritasFonteItem";
import { IFonteActionsProps } from "../FonteActions";

interface IProps {
    favoritas: IOrdenacaoFonteItem[];
    solicitacaoId: number;
    categoriaId: number | null;
    onRemoveFavorita: (item: IOrdenacaoFonteItem) => void;
    onOrderChange: (categoria: IOrdenacaoCategoriaItem) => void;
    fonteActionsProps: Omit<IFonteActionsProps, "item" | "onToggleFavorite" | "favorita" | "solicitacaoId">;
    onUpdateFonteFavorita: (data: IUpdateFonteFavorita) => Promise<void>;
    onUpdateOrdenacaoFontes: (data: IUpdateOrdenacaoFontes) => Promise<IOrdenacaoCategoriaItem | null>;
}

export default function OrdenacaoList({
    favoritas,
    solicitacaoId,
    categoriaId,
    onRemoveFavorita,
    onOrderChange,
    fonteActionsProps,
    onUpdateFonteFavorita,
    onUpdateOrdenacaoFontes,
}: IProps) {
    const [items, setItems] = useState<IOrdenacaoFonteItem[]>([]);

    const isInternalUpdateRef = useRef(false);
    const initializedRef = useRef(false);

    const sensors = useSensors(
        useSensor(MouseSensor, {
            activationConstraint: {
                distance: 4,
            },
        }),

        useSensor(TouchSensor, {
            activationConstraint: {
                delay: 150,
                tolerance: 5,
            },
        }),
    );

    const handleRemoveFavorite = async (
        item: IOrdenacaoFonteItem,
    ) => {
        try {

            isInternalUpdateRef.current = true;

            setItems((oldItems) =>
                oldItems.filter(
                    (e) => e.numero !== item.numero,
                ),
            );

            onRemoveFavorita({
                ...item,
                favorita: false,
            });

        } catch (error) {
            EmitirErro(error);
        }
    };

    const handleSave = async () => {
        try {
            if (!categoriaId || items.length === 0) {
                return;
            }

            const categoriaOrdenada = await onUpdateOrdenacaoFontes({
                numerosFontes: items.map((e) => e.numero),
                solicitacaoId,
                categoriaId,
            });

            if (!categoriaOrdenada) {
                return;
            }

            onOrderChange(categoriaOrdenada);

            return categoriaOrdenada;

        } catch (error) {
            EmitirErro(error);
        }
    };

    const handleDragEnd = (
        event: DragEndEvent,
    ) => {

        const { active, over } = event;

        if (!over || active.id === over.id) {
            return;
        }

        setItems((oldItems) => {

            isInternalUpdateRef.current = true;

            const oldIndex = oldItems.findIndex(
                (item) => item.numero === active.id,
            );

            const newIndex = oldItems.findIndex(
                (item) => item.numero === over.id,
            );

            return arrayMove(
                oldItems,
                oldIndex,
                newIndex,
            );
        });
    };

    useEffect(() => {
        initializedRef.current = false;
        isInternalUpdateRef.current = false;

        setItems(favoritas);
    }, [categoriaId]);

    useEffect(() => {

        if (!initializedRef.current) {
            initializedRef.current = true;
            return;
        }

        setItems((currentItems) => {

            const currentOrderMap = new Map(
                currentItems.map((item, index) => [
                    item.numero,
                    index,
                ]),
            );

            const hasLengthChanged =
                favoritas.length !== currentItems.length;

            if (hasLengthChanged) {
                isInternalUpdateRef.current = true;
            }

            const sortedItems = [...favoritas].sort((a, b) => {

                const indexA = currentOrderMap.get(a.numero);
                const indexB = currentOrderMap.get(b.numero);

                if (indexA === undefined && indexB === undefined) {
                    return 0;
                }

                if (indexA === undefined) {
                    return 1;
                }

                if (indexB === undefined) {
                    return -1;
                }

                return indexA - indexB;
            });

            return sortedItems;
        });

    }, [favoritas]);

    useEffect(() => {

        if (!initializedRef.current) {
            return;
        }

        if (!isInternalUpdateRef.current) {
            return;
        }

        isInternalUpdateRef.current = false;

        handleSave();

    }, [items]);

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
            modifiers={[
                restrictToVerticalAxis,
                restrictToParentElement,
            ]}
        >
            <SortableContext
                items={items.map((e) => e.numero)}
                strategy={verticalListSortingStrategy}
            >
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        padding: 8,
                        gap: 8,
                    }}
                >
                    {
                        items.map((e, index) => (
                            <FavoritasFonteItem
                                key={e.numero}
                                item={e}
                                solicitacaoId={solicitacaoId}
                                onChangeFavorita={handleRemoveFavorite}
                                index={index}
                                fonteActionsProps={fonteActionsProps}
                                onUpdateFonteFavorita={onUpdateFonteFavorita}
                            />
                        ))
                    }
                </div>
            </SortableContext>
        </DndContext>
    );
};
